import { MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bool, mixed, number, object, string } from 'yup';
import { useUpdateDevice } from '~/api/devices';
import { DetailSelect, DetailSwitch, DetailTextField } from '~/components/forms/details';
import type { FormTableData } from '~/components/settings';
import { useAppContext } from '~/contexts';
import {
  DeviceGameEventConsoleType,
  DeviceGameEventSport,
  DevicePtpService,
  DeviceSerialDataSource,
} from '~/generated/graphql';
import type { DeviceServicesPage__Device as Device } from '../../queries/Services.generated';

const consoleTypes = Object.values(DeviceGameEventConsoleType);
const ptpServices = Object.values(DevicePtpService);
const serialDataSources = Object.values(DeviceSerialDataSource);
const sports = Object.values(DeviceGameEventSport);

const consoleTypeLabels = (t: TFunction) => ({
  [DeviceGameEventConsoleType.DaktronicsAllSport5000]: t(
    'pages.devices.show.services.game_event_console_type.daktronics_all_sport_5000',
  ),
  [DeviceGameEventConsoleType.DaktronicsDsti]: t(
    'pages.devices.show.services.game_event_console_type.daktronics_dsti',
  ),
  [DeviceGameEventConsoleType.Oes]: t('pages.devices.show.services.game_event_console_type.oes'),
  [DeviceGameEventConsoleType.ScorePad]: t(
    'pages.devices.show.services.game_event_console_type.score_pad',
  ),
});

const ptpServiceLabels = (t: TFunction) => ({
  [DevicePtpService.Follower]: t('pages.devices.show.services.ptp_service.follower'),
  [DevicePtpService.Leader]: t('pages.devices.show.services.ptp_service.leader'),
});

const serialDataSourceLabels = (t: TFunction) => ({
  [DeviceSerialDataSource.Embedded]: t('pages.devices.show.services.serial_data_source.embedded'),
  [DeviceSerialDataSource.Network]: t('pages.devices.show.services.serial_data_source.network'),
  [DeviceSerialDataSource.Usb]: t('pages.devices.show.services.serial_data_source.usb'),
});

const sportLabels = (t: TFunction) => ({
  [DeviceGameEventSport.Baseball]: t('pages.devices.show.services.game_event_sport.baseball'),
  [DeviceGameEventSport.Basketball]: t('pages.devices.show.services.game_event_sport.basketball'),
  [DeviceGameEventSport.Football]: t('pages.devices.show.services.game_event_sport.football'),
  [DeviceGameEventSport.Hockey]: t('pages.devices.show.services.game_event_sport.hockey'),
  [DeviceGameEventSport.Soccer]: t('pages.devices.show.services.game_event_sport.soccer'),
});

const emptyToNull = (value: unknown) => value || null;

const sortSectionsByHeading = (sections: FormTableData[][]) =>
  sections.sort(([a], [b]) => String(a.heading).localeCompare(String(b.heading))).flat();

const validationSchema = object({
  api: bool().required(),
  cacheServer: bool().required(),
  encryptionServer: bool().required(),
  gameEventConsoleType: mixed<DeviceGameEventConsoleType>()
    .oneOf(consoleTypes)
    .nullable()
    .transform(emptyToNull),
  gameEventSerialServerHost: string().trim().nullable().transform(emptyToNull),
  gameEventSport: mixed<DeviceGameEventSport>().oneOf(sports).nullable().transform(emptyToNull),
  gameEvents: bool().required(),
  jpegxsEncoder: bool().required(),
  jpegxsEncoderBpp: number().positive().required(),
  jpegxsEncoderTtl: number().integer().min(1).max(255).required(),
  jpegxsEncoderUrl: string().trim().nullable().transform(emptyToNull),
  messagingProxy: bool().required(),
  ntpServer: bool().required(),
  ptpService: mixed<DevicePtpService>().oneOf(ptpServices).nullable().transform(emptyToNull),
  publisher: bool().required(),
  scoreServer: bool().required(),
  serialDataSource: mixed<DeviceSerialDataSource>()
    .oneOf(serialDataSources)
    .nullable()
    .transform(emptyToNull),
  serialNetworkUrl: string().trim().nullable().transform(emptyToNull),
  serialPortSpeed: number().integer().positive().required(),
  serialServer: bool().required(),
  snmpService: bool().required(),
  sshService: bool().required(),
  streamAnalysis: bool().required(),
  transcodeServer: bool().required(),
});

interface UseServicesFormProps {
  device: Device;
}

export const useServicesForm = ({ device }: UseServicesFormProps) => {
  const { t } = useTranslation();
  const [updateDevice] = useUpdateDevice();
  const { currentNetwork, currentUser } = useAppContext();
  const isLinux = device.kind === 'LINUX';

  const initialValues = useMemo(
    () => ({
      api: device.api,
      cacheServer: device.cacheServer,
      encryptionServer: device.encryptionServer,
      gameEventConsoleType: device.gameEventConsoleType ?? '',
      gameEventSerialServerHost: device.gameEventSerialServerHost ?? '',
      gameEventSport: device.gameEventSport ?? '',
      gameEvents: device.gameEvents,
      jpegxsEncoder: device.jpegxsEncoder,
      jpegxsEncoderBpp: device.jpegxsEncoderBpp,
      jpegxsEncoderTtl: device.jpegxsEncoderTtl,
      jpegxsEncoderUrl: device.jpegxsEncoderUrl ?? '',
      messagingProxy: device.messagingProxy,
      ntpServer: device.ntpServer,
      ptpService: device.ptpService ?? '',
      publisher: device.publisher,
      scoreServer: device.scoreServer,
      serialDataSource: device.serialDataSource ?? '',
      serialNetworkUrl: device.serialNetworkUrl ?? '',
      serialPortSpeed: device.serialPortSpeed,
      serialServer: device.serialServer,
      snmpService: device.snmpService,
      sshService: device.sshService,
      streamAnalysis: device.streamAnalysis,
      transcodeServer: device.transcodeServer,
    }),
    [device],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      const { sshService, ...rest } = validationSchema.cast(values);
      const patch = isLinux
        ? { ...rest, ...(currentUser.admin ? { sshService } : {}) }
        : { api: rest.api };
      await updateDevice({ variables: { deviceId: device.id, patch } });
    },
    validateOnMount: true,
    validationSchema,
  });

  const { edgeServicesSettings, systemSettings, venueIntegrationsSettings } = useMemo(() => {
    const switchSetting = (
      name: keyof typeof formik.values,
      heading: string,
      subHeading: string,
      disabled = false,
    ): FormTableData => ({
      heading,
      subHeading,
      dataField: (
        <DetailSwitch
          checked={!!formik.values[name]}
          color="primary"
          disabled={formik.isSubmitting || disabled}
          name={name}
          onChange={formik.handleChange}
        />
      ),
    });

    // Turning SNMP off stays possible; turning it on needs the network's community.
    const snmpUnavailable = !currentNetwork.snmpConfigured && !device.snmpService;

    const edgeServicesSections: FormTableData[][] = [
      [
        switchSetting(
          'cacheServer',
          t('pages.devices.show.services.fields.cache_server.label'),
          t('pages.devices.show.services.fields.cache_server.description'),
        ),
      ],
      [
        switchSetting(
          'streamAnalysis',
          t('pages.devices.show.services.fields.stream_analysis.label'),
          t('pages.devices.show.services.fields.stream_analysis.description'),
        ),
      ],
      [
        switchSetting(
          'encryptionServer',
          t('pages.devices.show.services.fields.encryption_server.label'),
          t('pages.devices.show.services.fields.encryption_server.description'),
        ),
      ],
      [
        switchSetting(
          'transcodeServer',
          t('pages.devices.show.services.fields.transcode_server.label'),
          t('pages.devices.show.services.fields.transcode_server.description'),
        ),
      ],
      [
        switchSetting(
          'messagingProxy',
          t('pages.devices.show.services.fields.messaging_proxy.label'),
          t('pages.devices.show.services.fields.messaging_proxy.description'),
        ),
      ],
      [
        switchSetting(
          'publisher',
          t('pages.devices.show.services.fields.publisher.label'),
          t('pages.devices.show.services.fields.publisher.description'),
        ),
      ],
    ];

    const jpegxsEncoderSection = [
      switchSetting(
        'jpegxsEncoder',
        t('pages.devices.show.services.fields.jpegxs_encoder.label'),
        t('pages.devices.show.services.fields.jpegxs_encoder.description'),
      ),
    ];

    if (formik.values.jpegxsEncoder) {
      jpegxsEncoderSection.push(
        {
          heading: t('pages.devices.show.services.fields.jpegxs_encoder_bpp.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.jpegxs_encoder_bpp.description'),
          dataField: (
            <DetailTextField
              aria-label="jpegxs encoder bits per pixel"
              disabled={formik.isSubmitting}
              error={!!formik.errors.jpegxsEncoderBpp}
              inputProps={{ step: 0.1 }}
              name="jpegxsEncoderBpp"
              type="number"
              value={formik.values.jpegxsEncoderBpp}
              onChange={formik.handleChange}
            />
          ),
        },
        {
          heading: t('pages.devices.show.services.fields.jpegxs_encoder_ttl.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.jpegxs_encoder_ttl.description'),
          dataField: (
            <DetailTextField
              aria-label="jpegxs encoder ttl"
              disabled={formik.isSubmitting}
              error={!!formik.errors.jpegxsEncoderTtl}
              name="jpegxsEncoderTtl"
              type="number"
              value={formik.values.jpegxsEncoderTtl}
              onChange={formik.handleChange}
            />
          ),
        },
        {
          heading: t('pages.devices.show.services.fields.jpegxs_encoder_url.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.jpegxs_encoder_url.description'),
          dataField: (
            <DetailTextField
              aria-label="jpegxs encoder url"
              disabled={formik.isSubmitting}
              name="jpegxsEncoderUrl"
              value={formik.values.jpegxsEncoderUrl}
              onChange={formik.handleChange}
            />
          ),
        },
      );
    }

    edgeServicesSections.push(jpegxsEncoderSection);

    const gameEventsSection = [
      switchSetting(
        'gameEvents',
        t('pages.devices.show.services.fields.game_events.label'),
        t('pages.devices.show.services.fields.game_events.description'),
      ),
    ];

    if (formik.values.gameEvents) {
      gameEventsSection.push(
        {
          heading: t('pages.devices.show.services.fields.game_event_console_type.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.game_event_console_type.description'),
          dataField: (
            <DetailSelect
              disabled={formik.isSubmitting}
              displayEmpty
              name="gameEventConsoleType"
              value={formik.values.gameEventConsoleType}
              onChange={formik.handleChange}
            >
              <MenuItem value="">{t('pages.devices.show.services.none')}</MenuItem>
              {consoleTypes.map((value) => (
                <MenuItem key={value} value={value}>
                  {consoleTypeLabels(t)[value]}
                </MenuItem>
              ))}
            </DetailSelect>
          ),
        },
        {
          heading: t('pages.devices.show.services.fields.game_event_sport.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.game_event_sport.description'),
          dataField: (
            <DetailSelect
              disabled={formik.isSubmitting}
              displayEmpty
              name="gameEventSport"
              value={formik.values.gameEventSport}
              onChange={formik.handleChange}
            >
              <MenuItem value="">{t('pages.devices.show.services.none')}</MenuItem>
              {sports.map((value) => (
                <MenuItem key={value} value={value}>
                  {sportLabels(t)[value]}
                </MenuItem>
              ))}
            </DetailSelect>
          ),
        },
        {
          heading: t('pages.devices.show.services.fields.game_event_serial_server_host.label'),
          nested: true,
          subHeading: t(
            'pages.devices.show.services.fields.game_event_serial_server_host.description',
          ),
          dataField: (
            <DetailTextField
              disabled={formik.isSubmitting}
              name="gameEventSerialServerHost"
              value={formik.values.gameEventSerialServerHost}
              onChange={formik.handleChange}
            />
          ),
        },
      );
    }

    const serialServerSection = [
      switchSetting(
        'serialServer',
        t('pages.devices.show.services.fields.serial_server.label'),
        t('pages.devices.show.services.fields.serial_server.description'),
      ),
    ];

    if (formik.values.serialServer) {
      serialServerSection.push({
        heading: t('pages.devices.show.services.fields.serial_data_source.label'),
        nested: true,
        subHeading: t('pages.devices.show.services.fields.serial_data_source.description'),
        dataField: (
          <DetailSelect
            aria-label="serial data source"
            disabled={formik.isSubmitting}
            displayEmpty
            name="serialDataSource"
            value={formik.values.serialDataSource}
            onChange={formik.handleChange}
          >
            <MenuItem value="">{t('pages.devices.show.services.none')}</MenuItem>
            {serialDataSources.map((value) => (
              <MenuItem key={value} value={value}>
                {serialDataSourceLabels(t)[value]}
              </MenuItem>
            ))}
          </DetailSelect>
        ),
      });

      if (formik.values.serialDataSource === DeviceSerialDataSource.Network) {
        serialServerSection.push({
          heading: t('pages.devices.show.services.fields.serial_network_url.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.serial_network_url.description'),
          dataField: (
            <DetailTextField
              aria-label="serial network url"
              disabled={formik.isSubmitting}
              name="serialNetworkUrl"
              value={formik.values.serialNetworkUrl}
              onChange={formik.handleChange}
            />
          ),
        });
      } else {
        serialServerSection.push({
          heading: t('pages.devices.show.services.fields.serial_port_speed.label'),
          nested: true,
          subHeading: t('pages.devices.show.services.fields.serial_port_speed.description'),
          dataField: (
            <DetailTextField
              aria-label="serial port speed"
              disabled={formik.isSubmitting}
              error={!!formik.errors.serialPortSpeed}
              name="serialPortSpeed"
              type="number"
              value={formik.values.serialPortSpeed}
              onChange={formik.handleChange}
            />
          ),
        });
      }
    }

    const venueIntegrationsSections = [
      gameEventsSection,
      serialServerSection,
      [
        switchSetting(
          'scoreServer',
          t('pages.devices.show.services.fields.score_server.label'),
          t('pages.devices.show.services.fields.score_server.description'),
        ),
      ],
    ];

    const apiSetting = switchSetting(
      'api',
      t('pages.devices.show.services.fields.api.label'),
      t('pages.devices.show.services.fields.api.description'),
    );

    if (!isLinux) {
      return {
        edgeServicesSettings: [],
        systemSettings: [apiSetting],
        venueIntegrationsSettings: [],
      };
    }

    const systemSections: FormTableData[][] = [
      [apiSetting],
      [
        switchSetting(
          'ntpServer',
          t('pages.devices.show.services.fields.ntp_server.label'),
          t('pages.devices.show.services.fields.ntp_server.description'),
        ),
      ],
      [
        switchSetting(
          'snmpService',
          t('pages.devices.show.services.fields.snmp_service.label'),
          snmpUnavailable
            ? t('pages.devices.show.services.fields.snmp_service.requires_community')
            : t('pages.devices.show.services.fields.snmp_service.description'),
          snmpUnavailable,
        ),
      ],
      [
        {
          heading: t('pages.devices.show.services.fields.ptp_service.label'),
          subHeading: t('pages.devices.show.services.fields.ptp_service.description'),
          dataField: (
            <DetailSelect
              disabled={formik.isSubmitting}
              displayEmpty
              name="ptpService"
              value={formik.values.ptpService}
              onChange={formik.handleChange}
            >
              <MenuItem value="">{t('pages.devices.show.services.none')}</MenuItem>
              {ptpServices.map((value) => (
                <MenuItem key={value} value={value}>
                  {ptpServiceLabels(t)[value]}
                </MenuItem>
              ))}
            </DetailSelect>
          ),
        },
      ],
    ];

    if (currentUser.admin) {
      systemSections.push([
        switchSetting(
          'sshService',
          t('pages.devices.show.services.fields.ssh_service.label'),
          t('pages.devices.show.services.fields.ssh_service.description'),
        ),
      ]);
    }

    return {
      edgeServicesSettings: sortSectionsByHeading(edgeServicesSections),
      systemSettings: sortSectionsByHeading(systemSections),
      venueIntegrationsSettings: sortSectionsByHeading(venueIntegrationsSections),
    };
  }, [currentNetwork.snmpConfigured, currentUser.admin, device.snmpService, formik, isLinux, t]);

  return { edgeServicesSettings, formik, systemSettings, venueIntegrationsSettings };
};
